import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { CurrentUserRankingModel } from 'models/ranking';
import { UserStakingResponse } from 'models/stake';
import { UserResponseModel } from 'models/user';
import { uniqueId } from 'utils/commonUtils';

export enum LanguageEnum {
  ENGLISH = 'en',
  CHINESE = 'zh',
  CAMBODIA = 'km',
  THAI = 'th',
  SPANISH = 'es',
  FRENCH = 'fr',
  TURKEY = 'tr',
  RUSSIA = 'ru',
}

export interface AuthState {
  isLoggedIn: boolean;
  currentUser: UserResponseModel | undefined;
  loading: boolean;
  language: LanguageEnum;
  userRanking: CurrentUserRankingModel | undefined;
  isDarkMode: boolean;
  tokenPrice: number;
  tbetPrice: number;
  stakeList: UserStakingResponse[];
  updateStakeListResult: string;
}

const initialState: AuthState = {
  isLoggedIn: false,
  currentUser: undefined,
  loading: false,
  language: LanguageEnum.ENGLISH,
  userRanking: undefined,
  isDarkMode: true,
  tokenPrice: 1,
  tbetPrice: 0,
  stakeList: [],
  updateStakeListResult: '',
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setIsLoggedIn: (state, action: PayloadAction<boolean>) => {
      state.isLoggedIn = action.payload;
    },
    setCurrentUser: (state, action: PayloadAction<UserResponseModel | undefined>) => {
      state.currentUser = action.payload;
    },
    setClearStateToLogout: (state) => {
      if (state.isLoggedIn) {
        window.location.reload();
      }
      state.currentUser = undefined;
      state.isLoggedIn = false;
    },
    setLoading: (state) => {
      state.loading = true;
    },
    resetLoading: (state) => {
      state.loading = false;
    },
    changeLanguage: (state, action: PayloadAction<LanguageEnum>) => {
      state.language = action.payload;
    },
    setCurrentUserRanking: (state, action: PayloadAction<CurrentUserRankingModel | undefined>) => {
      state.userRanking = action.payload;
    },
    setIsDarkMode: (state, action: PayloadAction<boolean>) => {
      state.isDarkMode = action.payload;
    },
    setTokenPrice: (state, action: PayloadAction<number>) => {
      state.tokenPrice = action.payload;
    },
    setTbetPrice: (state, action: PayloadAction<number>) => {
      state.tbetPrice = action.payload;
    },
    setStakeList: (state, action: PayloadAction<UserStakingResponse[]>) => {
      state.stakeList = action.payload;
    },
    setUpdateStakeListResult: (state) => {
      state.updateStakeListResult = uniqueId();
    },
  },
});

// NOTE: Actions
export const {
  setIsLoggedIn,
  setCurrentUser,
  setClearStateToLogout,
  setLoading,
  resetLoading,
  changeLanguage,
  setCurrentUserRanking,
  setIsDarkMode,
  setTokenPrice,
  setTbetPrice,
  setStakeList,
  setUpdateStakeListResult,
} = userSlice.actions;

// NOTE: Selectors
export const selectIsLoggedIn = (state: RootState) => state.user.isLoggedIn;
export const selectCurrentUser = (state: RootState) => state.user.currentUser;
export const selectLoading = (state: RootState) => state.user.loading;
export const selectCurrentLanguage = (state: RootState) => state.user.language;
export const selectUserRanking = (state: RootState) => state.user.userRanking;
export const selectIsDarkMode = (state: RootState) => state.user.isDarkMode;
export const selectTokenPrice = (state: RootState) => state.user.tokenPrice;
export const selectTbetPrice = (state: RootState) => state.user.tbetPrice;
export const selectStakeList = (state: RootState) => state.user.stakeList;
export const selectUpdateStakeListResult = (state: RootState) => state.user.updateStakeListResult;
// NOTE: Reducer
export default userSlice.reducer;
